import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Post, PostWithBody } from '@/lib/posts'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { delay } from '@/lib/motion'
import { formatPostDate } from '@/lib/dates'
import { getPostBySlug, getPosts, getRelatedPosts } from '@/lib/posts'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { RichText } from '@/components/blog/rich-text'
import { PostCard } from '@/components/blog/post-card'
import { Photo, photoForName } from '@/components/generals/photo'
import { PostCover } from '@/components/blog/post-cover'
import { Container } from '@/components/generals/container'
import { routes } from '@/constants/site'

interface ArticleProps {
    post: PostWithBody
    related: Post[]
}

const Article: FC<ArticleProps> = ({ post, related }): ReactNode => {
    return (
        <Layout>
            <Seo title={post.title} description={post.excerpt} type='article' ogImage={post.coverImage ?? undefined} />

            <article>
                <header id='article-header' className='pt-hero pb-10 md:pb-14'>
                    <Container size='narrow'>
                        <Link
                            href={routes.blog}
                            className='enter group inline-flex items-center gap-1.5 text-[13px] text-ink/60 transition-colors hover:text-ink'
                            style={delay(0)}
                        >
                            <ArrowLeft aria-hidden='true' strokeWidth={1.75} className='size-4 transition-transform duration-500 ease-soft group-hover:-translate-x-0.5' />
                            All articles
                        </Link>

                        <h1 className='type-hero enter mt-8' style={delay(100)}>
                            {post.title}
                        </h1>

                        <div className='enter mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 text-[13px] text-ink/60' style={delay(220)}>
                            {post.authorName && (
                                <span className='flex items-center gap-2.5'>
                                    <Photo id={photoForName(post.authorName)} sizes='36px' portrait className='size-9 shrink-0 rounded-full shadow-[0_0_0_2.5px_#fffdf8,0_3px_8px_-3px_rgb(40_38_34/0.4)]' />
                                    <span className='flex flex-col leading-tight'>
                                        <span className='font-medium text-ink'>{post.authorName}</span>
                                        {post.authorRole && <span>{post.authorRole}</span>}
                                    </span>
                                </span>
                            )}

                            <span className='flex items-center gap-1.5'>
                                <time dateTime={post.publishedAt}>{formatPostDate(post.publishedAt)}</time>
                                <span aria-hidden='true'>·</span>
                                <span>{post.readingMinutes} min read</span>
                                <span aria-hidden='true'>·</span>
                                <span>{post.category}</span>
                            </span>
                        </div>
                    </Container>
                </header>

                <Container className='max-w-[calc(920px+4rem)]'>
                    <PostCover post={post} priority className='enter-fade aspect-[16/9]' />
                </Container>

                <Container size='narrow'>
                    <RichText html={post.body} className='pb-14 pt-12 md:pb-[90px] md:pt-16' />
                </Container>
            </article>

            {related.length > 0 && (
                <section id='keep-reading' aria-labelledby='keep-reading-title' className='border-t border-ink/15 py-14 md:py-[90px]'>
                    <Container>
                        <div className='flex items-end justify-between gap-6'>
                            <h2 id='keep-reading-title' className='type-section reveal'>
                                Keep reading
                            </h2>
                            <Link href={routes.blog} className='reveal text-[13.5px] text-ink/60 underline decoration-ink/25 underline-offset-4 transition-colors hover:text-ink'>
                                All articles
                            </Link>
                        </div>

                        <div className='reveal mt-10 grid gap-12 sm:grid-cols-2 sm:gap-8' style={delay(120)}>
                            {related.map(item => (
                                <PostCard key={item.id} post={item} />
                            ))}
                        </div>
                    </Container>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const posts = await getPosts()

        return { paths: posts.map(post => ({ params: { slug: post.slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Could not list blog posts:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ArticleProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true, revalidate: 60 }

    try {
        const post = await getPostBySlug(slug)

        if (!post) return { notFound: true, revalidate: 60 }

        const related = await getRelatedPosts(post, 2).catch(() => [])

        return { props: { post, related }, revalidate: 60 }
    }
    catch (error) {
        console.error(`Could not load the blog post "${slug}":`, error instanceof Error ? error.message : error)

        return { notFound: true, revalidate: 30 }
    }
}

export default Article
